import { setLocale } from 'yup';
import registerYupMethods from './registerYupMethods';

// Minimal structural slice of an i18next instance — only `.t` is used. Deliberately NOT
// i18next's own `i18n` type: apps can compile against a different hoisted i18next major
// than this library (e.g. partner-dashboard bundles v25 while the root hoists v22), and
// the nominal types are not mutually assignable across majors.
type I18nLike = {
  t: (key: string, params: Record<string, unknown>) => unknown;
};

// Register custom Yup methods once, at module load, so they exist before any schema is built.
registerYupMethods();

/**
 * Installs translated defaults for Yup's generic validation messages, resolving through
 * the given i18next instance from the `components:Global.Validations` namespace.
 *
 * MUST be called at MODULE scope of the app's i18n init file — never from a component or
 * an effect. Yup captures locale messages as default parameters at schema-CONSTRUCTION
 * time (`required(message = locale.mixed.required)`), and `setLocale` only mutates the
 * locale object for schemas built afterwards. React flushes child effects before parent
 * effects, so a `setLocale` inside an App-level effect runs AFTER any schema built during
 * the first render — those schemas keep Yup's hardcoded English default (`'${path} is a
 * required field'`) forever. Module scope in the i18n init runs before any component
 * module can build a schema.
 *
 * Nothing is lost by leaving React: the messages are functions that call `i18n.t` at
 * VALIDATION time, and i18next resolves the current language per call, so language
 * switches still work.
 */
export default function setYupLocale(i18n: I18nLike) {
  const t = (key: string, params: Record<string, unknown> = {}) =>
    i18n.t(`components:Global.Validations.${key}`, params);

  setLocale({
    mixed: {
      default: ({ label }) => (label ? t('invalidWithLabel', { label }) : t('invalid')),
      required: ({ label }) => (label ? t('requiredWithLabel', { label }) : t('required')),
      notType: ({ label }) => (label ? t('invalidWithLabel', { label }) : t('invalid')),
      oneOf: ({ label, values }) => (label ? t('oneOfWithLabel', { label, values }) : t('oneOf', { values })),
    },
    string: {
      email: ({ label }) => (label ? t('emailWithLabel', { label }) : t('email')),
      url: ({ label }) => (label ? t('urlWithLabel', { label }) : t('url')),
      min: ({ label, min }) => (label ? t('stringMinWithLabel', { label, min }) : t('stringMin', { min })),
      max: ({ label, max }) => (label ? t('stringMaxWithLabel', { label, max }) : t('stringMax', { max })),
      length: ({ label, length }) =>
        label ? t('stringLengthWithLabel', { label, length }) : t('stringLength', { length }),
    },
    number: {
      min: ({ label, min }) => (label ? t('numberMinWithLabel', { label, min }) : t('numberMin', { min })),
      max: ({ label, max }) => (label ? t('numberMaxWithLabel', { label, max }) : t('numberMax', { max })),
    },
  });
}
